/**
 * Node Editor Component
 *
 * Form for creating and editing graph nodes
 */
import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { knowledgeGraphStore } from '@/stores/KnowledgeGraphStore'
import { NodeType, GraphNode, GraphNodeCreate, GraphNodeUpdate } from '@/types'
import toast from 'react-hot-toast'

interface NodeEditorProps {
  graphId: string
  node?: GraphNode // If provided, we're editing; otherwise creating
  onClose: () => void
  onSave?: (node: GraphNode) => void
}

const NodeEditor: React.FC<NodeEditorProps> = observer(({ graphId, node, onClose, onSave }) => {
  const [nodeType, setNodeType] = useState<NodeType>(node?.node_type || NodeType.CONCEPT)
  const [label, setLabel] = useState(node?.label || '')
  const [properties, setProperties] = useState<Record<string, any>>(node?.properties || {})
  const [propertyKey, setPropertyKey] = useState('')
  const [propertyValue, setPropertyValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const handleAddProperty = () => {
    if (propertyKey.trim()) {
      setProperties({ ...properties, [propertyKey]: propertyValue })
      setPropertyKey('')
      setPropertyValue('')
    }
  }

  const handleRemoveProperty = (key: string) => {
    const newProps = { ...properties }
    delete newProps[key]
    setProperties(newProps)
  }

  const handleSave = async () => {
    if (!label.trim()) {
      toast.error('Label is required')
      return
    }

    setIsSaving(true)

    try {
      if (node) {
        // Update existing node
        const updateData: GraphNodeUpdate = {
          label: label.trim(),
          properties,
        }
        const updatedNode = await knowledgeGraphStore.updateNode(graphId, node.id, updateData)
        toast.success('Node updated successfully')
        if (onSave && updatedNode) {
          onSave(updatedNode)
        }
      } else {
        // Create new node
        const createData: GraphNodeCreate = {
          node_type: nodeType,
          label: label.trim(),
          properties,
        }
        const newNode = await knowledgeGraphStore.createNode(graphId, createData)
        toast.success('Node created successfully')
        if (onSave && newNode) {
          onSave(newNode)
        }
      }
      onClose()
    } catch (error: any) {
      toast.error(error.message || 'Failed to save node')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {node ? 'Edit Node' : 'Create Node'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Node Type (only for new nodes) */}
              {!node && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Node Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={nodeType}
                    onChange={(e) => setNodeType(e.target.value as NodeType)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-ring sm:text-sm"
                  >
                    {Object.values(NodeType).map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Label <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Enter node label"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-ring sm:text-sm"
                />
              </div>

              {/* Properties */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Properties
                </label>
                <div className="space-y-2">
                  {Object.entries(properties).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                      <span className="text-sm font-medium text-gray-700 flex-shrink-0">{key}:</span>
                      <span className="text-sm text-gray-600 flex-1">{String(value)}</span>
                      <button
                        onClick={() => handleRemoveProperty(key)}
                        className="text-red-600 hover:text-red-600 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-2 flex gap-2">
                  <input
                    type="text"
                    value={propertyKey}
                    onChange={(e) => setPropertyKey(e.target.value)}
                    placeholder="Key"
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-ring sm:text-sm"
                  />
                  <input
                    type="text"
                    value={propertyValue}
                    onChange={(e) => setPropertyValue(e.target.value)}
                    placeholder="Value"
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-ring sm:text-sm"
                  />
                  <button
                    onClick={handleAddProperty}
                    disabled={!propertyKey.trim()}
                    className="px-3 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/90 disabled:bg-gray-300"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !label.trim()}
              className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 sm:ml-3 sm:w-auto disabled:bg-gray-300"
            >
              {isSaving ? 'Saving...' : node ? 'Update' : 'Create'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
})

NodeEditor.displayName = 'NodeEditor'

export default NodeEditor
